/**
 * Utilities for identifying and parsing OCC options symbols.
 *
 * OCC format: ROOT (1-6 chars) + YYMMDD (expiry) + C|P + strike*1000 (8 digits)
 * Example: SPY260213C00690000 → SPY, Feb 13 2026, Call, $690.00
 */

const OCC_REGEX = /^([A-Z]{1,6})(\d{6})([CP])(\d{8})$/;

export interface ParsedOption {
  raw: string;
  underlying: string;
  expiry: string; // formatted e.g. "Feb 13, 2026"
  expiryDate: Date;
  type: 'Call' | 'Put';
  strike: number;
  /** Human-readable label e.g. "SPY $690 Call 2/13" */
  label: string;
}

export function isOptionSymbol(symbol: string): boolean {
  return OCC_REGEX.test(symbol);
}

export function parseOptionSymbol(symbol: string): ParsedOption | null {
  const m = symbol.match(OCC_REGEX);
  if (!m) return null;

  const [, underlying, dateStr, cp, strikeStr] = m;
  const year = 2000 + parseInt(dateStr.slice(0, 2), 10);
  const month = parseInt(dateStr.slice(2, 4), 10) - 1;
  const day = parseInt(dateStr.slice(4, 6), 10);
  const expiryDate = new Date(year, month, day);
  const type = cp === 'C' ? 'Call' : 'Put';
  const strike = parseInt(strikeStr, 10) / 1000;

  const expiry = expiryDate.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  const shortDate = `${month + 1}/${day}`;
  const label = `${underlying} $${strike} ${type} ${shortDate}`;

  return { raw: symbol, underlying, expiry, expiryDate, type, strike, label };
}
